import React from "react";

import { Clear as ClearIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";

import dayjs from "dayjs";

import { StopTimeRuleExceptionSchema } from "../../types";

export interface DeadlineRowProps {
  isEditing: boolean;
  deadline: StopTimeRuleExceptionSchema;
  onDelete?: () => void;
  onUpdate?: (deadline: StopTimeRuleExceptionSchema) => void;
}

const DeadlineRow: React.FC<DeadlineRowProps> = ({
  isEditing,
  deadline,
  onDelete,
  onUpdate,
}) => {
  const theme = useTheme();

  const handleDateChange = (
    date: dayjs.Dayjs | null,
    field: "for_date" | "deadline_date",
  ) => {
    if (!date || !onUpdate) return;
    onUpdate({
      ...deadline,
      [field]: date.format("YYYY-MM-DD"),
    });
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (!time || !onUpdate) return;
    onUpdate({
      ...deadline,
      hour: time.hour(),
      minute: time.minute(),
    });
  };

  return isEditing ? (
    <Stack key={deadline.for_date} direction="row" gap={1} gridColumn="span 4">
      <DatePicker
        slotProps={{
          textField: {
            size: "small",
            sx: {
              flex: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.common.white,
            },
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2">Gäller</Typography>
                </InputAdornment>
              ),
            },
          },
        }}
        value={dayjs(deadline.for_date)}
        onChange={(date) => handleDateChange(date, "for_date")}
      />
      <DatePicker
        slotProps={{
          textField: {
            size: "small",
            sx: {
              flex: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.common.white,
            },
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2">Stoppdag</Typography>
                </InputAdornment>
              ),
            },
          },
        }}
        value={dayjs(deadline.deadline_date)}
        onChange={(date) => handleDateChange(date, "deadline_date")}
      />
      <TimePicker
        slotProps={{
          textField: {
            size: "small",
            sx: {
              flex: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.common.white,
            },
          },
        }}
        value={dayjs()
          .set("hour", deadline.hour)
          .set("minute", deadline.minute)}
        onChange={handleTimeChange}
      />

      <Button
        endIcon={<ClearIcon />}
        size="small"
        sx={{
          color: theme.palette.grey[600],
          textTransform: "none",
          width: "fit-content",
        }}
        variant="text"
        onClick={onDelete}
      >
        Ta bort
      </Button>
    </Stack>
  ) : (
    <Box
      key={deadline.for_date}
      display="grid"
      gridColumn="span 4"
      gridTemplateColumns="subgrid"
    >
      <Typography variant="body2">{deadline.for_date}</Typography>
      <Typography variant="body2">{deadline.deadline_date}</Typography>
      <Typography variant="body2">{`${deadline.hour}:${deadline.minute.toString().padStart(2, "0")}`}</Typography>
    </Box>
  );
};

export default DeadlineRow;
